// Full-page product detail view with variant dropdowns and quantity selector.
// Accepts a single unified product shape — Shopify data is normalized upstream.

import { formatPrice } from "@/lib/queries/products";
import { buildConditionBadges, type ConditionCriteria } from "@/lib/conditions";
import { isVariantOutOfStock as isVariantStockDepleted } from "./product-utils";
import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import { cn } from "@repo/ui/lib/utils";
import DOMPurify from "dompurify";
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Minus,
  Plus,
  ShoppingCart,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

interface VariantOption {
  type: string;
  values: string[];
}

interface ProductVariant {
  id: number;
  externalId?: string | null;
  name?: string | null;
  price?: string | number | null;
  sku?: string | null;
  images?: string[] | null;
  optionType1?: string | null;
  optionValue1?: string | null;
  optionType2?: string | null;
  optionValue2?: string | null;
  optionType3?: string | null;
  optionValue3?: string | null;
  optionType4?: string | null;
  optionValue4?: string | null;
  inventory?: {
    inventoryQuantity?: number | null;
  } | null;
  sellWhenOutOfStock?: boolean;
}

interface VariantCartInfo {
  cartItemId: string;
  quantity: number;
}

interface ProductDetailViewProps {
  product?: {
    name?: string | null;
    description?: string | null;
    descriptionHtml?: string | null;
    images?: string[] | null;
    productVariants?: ProductVariant[] | null;
    totalVariants?: number;
  } | null;
  isLoading: boolean;
  /** Error from fetching product details. When set, shows an error message instead of loading. */
  error?: Error | null;
  showPrice: boolean;
  /** When false, hides the Vendor badge. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, hides Tag badges. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  currencyCode: string;
  cartVariantIds: Set<number>;
  onAddToCart: (variant: ProductVariant, quantity: number) => void;
  onBack: () => void;
  maxQuantity?: number | null;
  currentCartQuantity?: number;
  /** Total inventory from the product listing. When 0, add-to-cart is disabled. */
  totalInventory?: number;
  /** Whether inventory is tracked for this product. When false, product is always in stock. */
  tracksInventory?: boolean;
  /** Category segments and collection names for this product. */
  filterLabels?: string[];
  /** Called when a filter breadcrumb is clicked — applies the filter and navigates back to the grid. */
  onApplyFilter?: (label: string) => void;
  /** Product metadata for condition badge display. */
  vendor?: string | null;
  tags?: string[] | null;
  productType?: string | null;
  /** Condition criteria for highlighting matching metadata badges. */
  conditionCriteria?: ConditionCriteria;
  /** Map from variant ID to cart item info. Used for in-place cart editing on the detail view. */
  variantCartInfo?: Map<number, VariantCartInfo>;
  /** Update quantity of a cart item by its ID. */
  onUpdateCartQuantity?: (cartItemId: string, quantity: number) => void;
  /** Remove a cart item by its ID. */
  onRemoveCartItem?: (cartItemId: string) => void;
  /** Unfulfilled conditions this product satisfies. */
  matchingConditionNames?: string[];
  /** Minimum stock level from brand inventory settings. Variants with inventory <= this are OOS. */
  minimumStockLevel?: number;
  /** Remaining catalog inventory (maxInventoryLimit - ordered - inCart). */
  catalogInventoryRemaining?: number;
  /** The maximum inventory limit set by the brand for this product. */
  maxInventoryLimit?: number;
}

// Per Shopify documentation: "The 'variant' for a product with no variants always
// displays 'title': 'Default Title' as the default variant title, and
// 'option1': 'Default Title' as the default variant option. These attributes
// aren't displayed to your customers, and get overwritten when you create variant
// options and values for the product."
// We detect this sentinel and suppress the variant selector entirely so the user
// doesn't need to make a meaningless selection before adding to cart.
function isDefaultTitleOnlyOption(options: VariantOption[]): boolean {
  return (
    options.length === 1 &&
    options[0].type === "Title" &&
    options[0].values.length === 1 &&
    options[0].values[0] === "Default Title"
  );
}

// Extract unique variant option dimensions from product variants
function extractVariantOptions(variants: ProductVariant[]): VariantOption[] {
  const dimensionMap = new Map<string, Set<string>>();

  for (const v of variants) {
    const pairs = [
      [v.optionType1, v.optionValue1],
      [v.optionType2, v.optionValue2],
      [v.optionType3, v.optionValue3],
      [v.optionType4, v.optionValue4],
    ] as const;

    for (const [type, value] of pairs) {
      if (type && value) {
        if (!dimensionMap.has(type)) {
          dimensionMap.set(type, new Set());
        }
        dimensionMap.get(type)?.add(value);
      }
    }
  }

  return Array.from(dimensionMap.entries()).map(([type, values]) => ({
    type,
    values: Array.from(values),
  }));
}

// True when a variant is consistent with the currently-selected options.
// Every selection must be explicitly present on the variant (matching type AND
// value), so a variant that lacks a selected dimension is rejected rather than
// silently passing. Selections is a subset of the variant's dimensions, so a
// partial selection (e.g. color chosen but not size) still matches.
function variantMatchesSelections(
  v: ProductVariant,
  selections: Record<string, string>,
): boolean {
  const pairs = [
    [v.optionType1, v.optionValue1],
    [v.optionType2, v.optionValue2],
    [v.optionType3, v.optionValue3],
    [v.optionType4, v.optionValue4],
  ] as const;

  return Object.entries(selections).every(([selectedType, selectedValue]) =>
    pairs.some(
      ([type, value]) => type === selectedType && value === selectedValue,
    ),
  );
}

// Find variant matching selected options.
// Returns null unless every dimension present in variantOptions has a selection.
function findMatchingVariant(
  variants: ProductVariant[],
  selections: Record<string, string>,
  variantOptions: VariantOption[],
): ProductVariant | null {
  if (
    variantOptions.length > 0 &&
    !variantOptions.every((opt) => selections[opt.type])
  ) {
    return null;
  }

  return variants.find((v) => variantMatchesSelections(v, selections)) ?? null;
}

// Resolve the variant whose image to display, allowing partial selections.
// The image should update as soon as any option (e.g. color/pattern) is picked,
// without waiting for every dimension — so this ignores the all-selected guard
// that findMatchingVariant applies for cart/price/stock. Prefers the first
// matching variant that actually has an image. (EPD-11297)
function findImageVariant(
  variants: ProductVariant[],
  selections: Record<string, string>,
): ProductVariant | null {
  const matching = variants.filter((v) =>
    variantMatchesSelections(v, selections),
  );
  return matching.find((v) => v.images?.some(Boolean)) ?? matching[0] ?? null;
}

// Adapts this view's variant shape onto the canonical rule in product-utils, so
// the grid card and the detail view can never drift apart again (EPD-10274).
function isVariantOutOfStock(
  variant: ProductVariant,
  tracksInventory?: boolean,
  minimumStockLevel?: number,
): boolean {
  return isVariantStockDepleted(
    {
      inventoryQuantity: variant.inventory?.inventoryQuantity,
      sellWhenOutOfStock: variant.sellWhenOutOfStock,
    },
    tracksInventory,
    minimumStockLevel ?? 0,
  );
}

export function ProductDetailView({
  product,
  isLoading,
  error,
  showPrice,
  showVendor = true,
  showTags = true,
  currencyCode,
  cartVariantIds,
  onAddToCart,
  onBack,
  maxQuantity,
  currentCartQuantity = 0,
  totalInventory,
  tracksInventory,
  filterLabels,
  onApplyFilter,
  vendor,
  tags,
  productType,
  conditionCriteria,
  variantCartInfo,
  onUpdateCartQuantity,
  onRemoveCartItem,
  matchingConditionNames,
  minimumStockLevel = 0,
  catalogInventoryRemaining,
  maxInventoryLimit,
}: ProductDetailViewProps) {
  // Derive product-level out-of-stock from individual variants to respect sellWhenOutOfStock.
  // When variants have inventory data, check at the variant level so variants with
  // sellWhenOutOfStock=true are not incorrectly blocked by totalInventory=0.
  const isOutOfStock = useMemo(() => {
    // tracksInventory === false means "sell even when OOS" — never block.
    if (tracksInventory === false) return false;
    // When no threshold and not explicitly tracking, fall back to legacy behaviour.
    if (minimumStockLevel === 0 && tracksInventory !== true) return false;
    if (product?.productVariants?.length) {
      const hasInventoryData = product.productVariants.some(
        (v) => v.inventory?.inventoryQuantity != null,
      );
      if (hasInventoryData) {
        return product.productVariants.every((v) =>
          isVariantOutOfStock(v, tracksInventory, minimumStockLevel),
        );
      }
    }
    if (totalInventory == null) return false;
    return totalInventory <= minimumStockLevel;
  }, [product, tracksInventory, totalInventory, minimumStockLevel]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(
    () => new Set(),
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Memoize sanitized HTML description
  const sanitizedDescription = useMemo(() => {
    if (product?.descriptionHtml) {
      return DOMPurify.sanitize(product.descriptionHtml);
    }
    return "";
  }, [product?.descriptionHtml]);

  // Extract variant dimensions, suppressing Shopify's "Default Title" sentinel
  const variantOptions = useMemo(() => {
    if (product?.productVariants) {
      const options = extractVariantOptions(product.productVariants);
      // Suppress the "Title: Default Title" sentinel — it's not a real user choice
      if (isDefaultTitleOnlyOption(options)) return [];
      return options;
    }
    return [];
  }, [product]);

  // Find the currently selected variant
  const selectedVariant = useMemo(() => {
    if (product?.productVariants) {
      if (product.productVariants.length === 1)
        return product.productVariants[0];
      if (variantOptions.length === 0 && product.productVariants.length > 0) {
        return product.productVariants[0];
      }
      return findMatchingVariant(
        product.productVariants,
        selections,
        variantOptions,
      );
    }
    return null;
  }, [product, selections, variantOptions]);

  // Variant used purely to drive the displayed image. Unlike selectedVariant,
  // this resolves on partial selections so the photo swaps as soon as a
  // color/pattern is chosen, before a size is picked. (EPD-11297)
  const imageVariant = useMemo(() => {
    if (!product?.productVariants?.length) return selectedVariant;
    if (product.productVariants.length === 1) return product.productVariants[0];
    // No selection yet — fall back to the product hero images. Return null
    // (not selectedVariant, which can be the first variant when there are no
    // option dimensions) so displayImages uses product.images.
    if (Object.keys(selections).length === 0) return null;
    return (
      findImageVariant(product.productVariants, selections) ?? selectedVariant
    );
  }, [product, selections, selectedVariant]);

  // Collect all images for the carousel (variant images take priority, fall back to product images)
  const displayImages = useMemo(() => {
    const variantImgs = imageVariant?.images?.filter(Boolean) ?? [];
    const productImgs = product?.images?.filter(Boolean) ?? [];
    const imgs = variantImgs.length > 0 ? variantImgs : productImgs;
    return imgs as string[];
  }, [imageVariant, product]);

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Reset carousel to first image whenever the image set changes (e.g. variant switch)
  const prevImagesRef = useRef(displayImages);
  if (prevImagesRef.current !== displayImages) {
    prevImagesRef.current = displayImages;
    setActiveImageIndex(0);
  }

  const title = product?.name;
  const inCart = selectedVariant
    ? cartVariantIds.has(selectedVariant.id)
    : false;

  // Get variant price
  const variantPrice = useMemo(() => {
    if (!selectedVariant) return null;
    return Number(selectedVariant.price ?? 0);
  }, [selectedVariant]);

  // Check if the selected variant is already in the cart
  const selectedVariantCartInfo = useMemo(() => {
    if (!selectedVariant || !variantCartInfo) return null;
    return variantCartInfo.get(selectedVariant.id) ?? null;
  }, [selectedVariant, variantCartInfo]);

  // Build a lookup to determine if a specific option value is entirely out of stock.
  const isOptionValueOutOfStock = useMemo(() => {
    if (product?.productVariants && tracksInventory === true) {
      const variants = product.productVariants;
      return (dimensionIndex: number, value: string): boolean => {
        const dim = variantOptions[dimensionIndex];
        if (!dim) return false;

        const matchingVariants = variants.filter((v) => {
          const pairs = [
            [v.optionType1, v.optionValue1],
            [v.optionType2, v.optionValue2],
            [v.optionType3, v.optionValue3],
            [v.optionType4, v.optionValue4],
          ] as const;

          const hasTargetValue = pairs.some(
            ([type, val]) => type === dim.type && val === value,
          );
          if (!hasTargetValue) return false;

          for (const opt of variantOptions) {
            if (opt.type === dim.type) continue;
            const sel = selections[opt.type];
            if (!sel) continue;
            const matchesSel = pairs.some(
              ([type, val]) => type === opt.type && val === sel,
            );
            if (!matchesSel) return false;
          }
          return true;
        });

        if (matchingVariants.length === 0) return false;
        return matchingVariants.every((v) =>
          isVariantOutOfStock(v, tracksInventory, minimumStockLevel),
        );
      };
    }

    return () => false;
  }, [product, variantOptions, selections, tracksInventory, minimumStockLevel]);

  // Check if the selected variant itself is out of stock
  const isSelectedVariantOutOfStock = useMemo(() => {
    if (!selectedVariant) return false;
    return isVariantOutOfStock(
      selectedVariant,
      tracksInventory,
      minimumStockLevel,
    );
  }, [selectedVariant, tracksInventory, minimumStockLevel]);

  // Check if all required selections are made
  const allSelected =
    variantOptions.length === 0 ||
    variantOptions.every((opt) => selections[opt.type]);

  const handleSelect = useCallback((dimension: string, value: string) => {
    setSelections((prev) => ({ ...prev, [dimension]: value }));
    setValidationErrors((prev) => {
      if (!prev.has(dimension)) return prev;
      const next = new Set(prev);
      next.delete(dimension);
      return next;
    });
  }, []);

  const exceedsCatalogInventory =
    (catalogInventoryRemaining != null &&
      (catalogInventoryRemaining <= 0 ||
        quantity > catalogInventoryRemaining)) ||
    (maxQuantity != null && currentCartQuantity + quantity > maxQuantity);

  // productVariants is explicitly an empty array — PFA returned variant metadata
  // but with no variant entries. Without a variant ID we cannot add to cart at all.
  const noVariantAvailable =
    product?.productVariants != null && product.productVariants.length === 0;

  // Reason text shown both in the Add-to-Cart tooltip (on hover) and as an
  // always-visible inline subtitle. Returns null when the button is enabled or
  // when a more specific subtitle (OOS / variant unavailable) is already in play.
  const addToCartDisabledMessage = useMemo((): string | null => {
    if (isOutOfStock || isSelectedVariantOutOfStock || noVariantAvailable) {
      return null;
    }
    if (catalogInventoryRemaining != null && maxInventoryLimit != null) {
      if (catalogInventoryRemaining <= 0) {
        return "This product is no longer available — please pick another";
      }
      if (quantity > catalogInventoryRemaining) {
        return `Only ${catalogInventoryRemaining} of this product remain — please reduce quantity`;
      }
    }
    if (maxQuantity != null && currentCartQuantity + quantity > maxQuantity) {
      return `You can request up to ${maxQuantity} of this product per order`;
    }
    return null;
  }, [
    isOutOfStock,
    isSelectedVariantOutOfStock,
    noVariantAvailable,
    catalogInventoryRemaining,
    maxInventoryLimit,
    quantity,
    maxQuantity,
    currentCartQuantity,
  ]);

  const addToCartLabel = noVariantAvailable
    ? "Unavailable"
    : isOutOfStock || isSelectedVariantOutOfStock
      ? "Out of Stock"
      : inCart
        ? "Add More to Cart"
        : "Add to Cart";

  const handleAddToCart = useCallback(() => {
    if (exceedsCatalogInventory) return;

    if (allSelected && selectedVariant) {
      setValidationErrors(new Set());
      onAddToCart(selectedVariant, quantity);
      setQuantity(1);
      return;
    }

    // Compute missing options and show validation errors
    const missing = new Set(
      variantOptions
        .filter((opt) => !selections[opt.type])
        .map((opt) => opt.type),
    );
    setValidationErrors(missing);

    // Scroll to the first missing option within the scrollable container.
    // Use manual container.scrollTo instead of el.scrollIntoView to avoid
    // scrolling ancestor elements (e.g. the Vaul drawer), which would create
    // blank space below the footer on mobile.
    const firstMissing = variantOptions.find((opt) => !selections[opt.type]);
    const container = scrollContainerRef.current;
    if (firstMissing && container) {
      const selectId = `variant-${firstMissing.type.replace(/\s+/g, "-").toLowerCase()}`;
      const el = container.querySelector<HTMLElement>(`[id="${selectId}"]`);
      if (el) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const scrollTop =
          container.scrollTop +
          elRect.top -
          containerRect.top -
          containerRect.height / 2;
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    }
  }, [
    exceedsCatalogInventory,
    allSelected,
    selectedVariant,
    variantOptions,
    selections,
    onAddToCart,
    quantity,
  ]);

  if (isLoading) {
    return (
      <ResponsiveDialog open={true} onOpenChange={(open) => !open && onBack()}>
        <ResponsiveDialogContent className="max-w-2xl px-0 pb-0 max-h-[90vh] flex flex-col overflow-hidden">
          <ResponsiveDialogHeader className="px-4 pt-6">
            <ResponsiveDialogTitle>
              <Skeleton className="h-6 w-48" />
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription className="sr-only">
              Loading product details
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <div className="px-4 py-4">
            <Skeleton className="aspect-square w-full rounded-lg mb-6" />
            <Skeleton className="h-5 w-24 mb-4" />
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    );
  }

  if (error || !product) {
    return (
      <ResponsiveDialog open={true} onOpenChange={(open) => !open && onBack()}>
        <ResponsiveDialogContent className="max-w-2xl px-0 pb-0 max-h-[90vh] flex flex-col overflow-hidden">
          <ResponsiveDialogHeader className="px-4 pt-6">
            <ResponsiveDialogTitle>Product Unavailable</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {error
                ? "We couldn\u2019t load this product. It may have been removed or is temporarily unavailable."
                : "This product could not be found."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter className="px-4 py-4">
            <Button variant="outline" onClick={onBack}>
              Go Back
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    );
  }

  return (
    <ResponsiveDialog open={true} onOpenChange={(open) => !open && onBack()}>
      <ResponsiveDialogContent className="max-w-2xl px-0 pb-0 max-h-[90vh] flex flex-col overflow-hidden">
        <ResponsiveDialogHeader className="px-4 pt-6">
          {/* Clickable category/collection breadcrumbs */}
          {filterLabels && filterLabels.length > 0 && onApplyFilter && (
            <nav
              aria-label="Product categories"
              className="flex items-center flex-wrap gap-1 text-xs text-muted-foreground mb-1"
            >
              {filterLabels.map((label, i) => (
                // Category path segments can repeat, index needed for uniqueness
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <span key={`${label}-${i}`} className="flex items-center">
                  {i > 0 && (
                    <ChevronRight className="h-3 w-3 mx-0.5 shrink-0" />
                  )}
                  <button
                    type="button"
                    className="hover:text-foreground hover:underline transition-colors"
                    onClick={() => onApplyFilter(label)}
                  >
                    {label}
                  </button>
                </span>
              ))}
            </nav>
          )}
          <ResponsiveDialogTitle className="text-xl font-semibold">
            {title || "Product Details"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            Product details for {title}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
        >
          {/* Product Image Carousel */}
          {displayImages.length > 0 ? (
            <div className="mb-6">
              {/* Main image */}
              <div className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                <img
                  src={displayImages[activeImageIndex]}
                  alt={`${title ?? ""} — image ${activeImageIndex + 1} of ${displayImages.length}`}
                  className="w-full h-full object-contain"
                />
                {displayImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous image"
                      onClick={() =>
                        setActiveImageIndex((i) =>
                          i === 0 ? displayImages.length - 1 : i - 1,
                        )
                      }
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-1 shadow transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next image"
                      onClick={() =>
                        setActiveImageIndex((i) =>
                          i === displayImages.length - 1 ? 0 : i + 1,
                        )
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-1 shadow transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {displayImages.map((src, idx) => (
                        <button
                          key={src}
                          type="button"
                          aria-label={`Go to image ${idx + 1}`}
                          onClick={() => setActiveImageIndex(idx)}
                          className={cn(
                            "h-1.5 rounded-full transition-all",
                            idx === activeImageIndex
                              ? "w-4 bg-foreground"
                              : "w-1.5 bg-foreground/40 hover:bg-foreground/70",
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* Thumbnails (only when 3+ images) */}
              {displayImages.length >= 3 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                  {displayImages.map((src, idx) => (
                    <button
                      key={src}
                      type="button"
                      aria-label={`View image ${idx + 1}`}
                      onClick={() => setActiveImageIndex(idx)}
                      className={cn(
                        "flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-colors",
                        idx === activeImageIndex
                          ? "border-foreground"
                          : "border-transparent hover:border-muted-foreground",
                      )}
                    >
                      <img
                        src={src}
                        alt={`${title ?? ""} thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mb-6">
              <div className="relative aspect-square bg-muted rounded-lg overflow-hidden flex flex-col items-center justify-center text-muted-foreground gap-3">
                <ImageIcon
                  className="h-16 w-16 text-muted-foreground/40"
                  strokeWidth={1.5}
                />
                <p className="text-sm">No image available</p>
              </div>
            </div>
          )}

          {/* Price */}
          <div className="mb-4">
            {showPrice && variantPrice != null && allSelected && (
              <p className="text-lg font-medium text-muted-foreground mt-1">
                {formatPrice(variantPrice, currencyCode)}
              </p>
            )}
          </div>

          {/* Description — prefer sanitized HTML, fall back to plain text */}
          {sanitizedDescription ? (
            /* biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify */
            <div
              className="text-sm text-muted-foreground mb-6 prose prose-sm max-w-none"
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
              dangerouslySetInnerHTML={{
                __html: sanitizedDescription,
              }}
            />
          ) : product?.description ? (
            <p className="text-sm text-muted-foreground mb-6">
              {product.description}
            </p>
          ) : null}

          {/* Category / collection labels (only when breadcrumbs aren't shown) */}
          {filterLabels && filterLabels.length > 0 && !onApplyFilter && (
            <div className="flex flex-wrap gap-1.5 mb-6">
              {filterLabels.map((label, i) => (
                <span
                  // Labels can repeat across segments, index needed for uniqueness
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${label}-${i}`}
                  className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          )}

          {/* Product metadata badges with condition highlighting
              (collections/category shown separately as breadcrumbs above) */}
          <ProductMetadataBadges
            vendor={vendor}
            tags={tags}
            productType={productType}
            showVendor={showVendor}
            showTags={showTags}
            conditionCriteria={conditionCriteria}
          />

          {/* Variant Selectors */}
          {variantOptions.length > 0 && (
            <div className="space-y-4 mb-6">
              {variantOptions.map((option, dimIndex) => {
                const selectId = `variant-${option.type.replace(/\s+/g, "-").toLowerCase()}`;
                return (
                  <div key={option.type}>
                    <label
                      htmlFor={selectId}
                      className="text-sm font-medium mb-1.5 block"
                    >
                      {option.type}
                    </label>
                    <Select
                      value={selections[option.type] || undefined}
                      onValueChange={(value: string) =>
                        handleSelect(option.type, value)
                      }
                    >
                      <SelectTrigger
                        id={selectId}
                        className={cn(
                          "w-full",
                          validationErrors.has(option.type) &&
                            "border-destructive",
                        )}
                        aria-invalid={validationErrors.has(option.type)}
                      >
                        <SelectValue
                          placeholder={`Select ${option.type.toLowerCase()}`}
                        />
                      </SelectTrigger>
                      <SelectContent position="item-aligned">
                        {option.values.map((val) => {
                          const oosVal = isOptionValueOutOfStock(dimIndex, val);
                          return (
                            <SelectItem key={val} value={val} disabled={oosVal}>
                              {val}
                              {oosVal && (
                                <span className="ml-2 text-muted-foreground">
                                  (Out of Stock)
                                </span>
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {validationErrors.has(option.type) && (
                      <p className="text-sm text-destructive mt-1">
                        Please select {option.type.toLowerCase()}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <ResponsiveDialogFooter className="flex-col gap-3 sm:flex-col sm:space-x-0 border-t p-4 bg-background shrink-0">
          {/* Condition match badges (deduplicated) */}
          {matchingConditionNames && matchingConditionNames.length > 0 && (
            <div className="flex flex-wrap gap-1.5 w-full">
              {[...new Set(matchingConditionNames)].map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200 px-2.5 py-0.5"
                >
                  <CheckCircle className="h-3 w-3" />
                  {name}
                </span>
              ))}
            </div>
          )}

          {selectedVariantCartInfo &&
          onUpdateCartQuantity &&
          onRemoveCartItem ? (
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">In Cart</label>
                  <p className="text-[10px] text-muted-foreground">
                    Adjust quantity or remove
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    aria-label="Decrease cart quantity"
                    onClick={() => {
                      if (selectedVariantCartInfo.quantity <= 1) {
                        onRemoveCartItem(selectedVariantCartInfo.cartItemId);
                      } else {
                        onUpdateCartQuantity(
                          selectedVariantCartInfo.cartItemId,
                          selectedVariantCartInfo.quantity - 1,
                        );
                      }
                    }}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span
                    className="text-base font-medium w-8 text-center"
                    aria-live="polite"
                  >
                    {selectedVariantCartInfo.quantity}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9"
                            aria-label="Increase cart quantity"
                            aria-describedby={
                              catalogInventoryRemaining != null &&
                              catalogInventoryRemaining <= 0
                                ? "cart-quantity-limit-message"
                                : undefined
                            }
                            onClick={() =>
                              onUpdateCartQuantity(
                                selectedVariantCartInfo.cartItemId,
                                selectedVariantCartInfo.quantity + 1,
                              )
                            }
                            disabled={
                              isSelectedVariantOutOfStock ||
                              (maxQuantity != null &&
                                currentCartQuantity >= maxQuantity) ||
                              (catalogInventoryRemaining != null &&
                                catalogInventoryRemaining <= 0)
                            }
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {catalogInventoryRemaining != null &&
                        catalogInventoryRemaining <= 0 &&
                        maxInventoryLimit != null && (
                          <TooltipContent>
                            <p>
                              This product is no longer available — please pick
                              another
                            </p>
                          </TooltipContent>
                        )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Quantity Selector */}
              <div className="w-full space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Quantity</label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      aria-label="Decrease quantity"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span
                      className="text-base font-medium w-8 text-center"
                      aria-live="polite"
                    >
                      {quantity}
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              aria-label="Increase quantity"
                              aria-describedby={
                                catalogInventoryRemaining != null &&
                                quantity >= catalogInventoryRemaining
                                  ? "quantity-limit-message"
                                  : undefined
                              }
                              onClick={() => setQuantity((q) => q + 1)}
                              disabled={
                                isSelectedVariantOutOfStock ||
                                (maxQuantity != null &&
                                  currentCartQuantity + quantity >=
                                    maxQuantity) ||
                                (catalogInventoryRemaining != null &&
                                  quantity >= catalogInventoryRemaining)
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {catalogInventoryRemaining != null &&
                          maxInventoryLimit != null &&
                          quantity >= catalogInventoryRemaining && (
                            <TooltipContent>
                              <p>
                                {catalogInventoryRemaining <= 0
                                  ? "This product is no longer available — please pick another"
                                  : `Only ${catalogInventoryRemaining} of this product remain — please reduce quantity`}
                              </p>
                            </TooltipContent>
                          )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              <div className="w-full">
                {/* Add to Cart Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex w-full">
                        <Button
                          className="w-full"
                          size="lg"
                          onClick={handleAddToCart}
                          aria-describedby={
                            addToCartDisabledMessage
                              ? "add-to-cart-disabled-message"
                              : undefined
                          }
                          disabled={
                            isOutOfStock ||
                            isSelectedVariantOutOfStock ||
                            exceedsCatalogInventory ||
                            noVariantAvailable
                          }
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          {addToCartLabel}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {addToCartDisabledMessage && (
                      <TooltipContent>
                        <p>{addToCartDisabledMessage}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                {!noVariantAvailable &&
                  (isOutOfStock || isSelectedVariantOutOfStock) && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {isOutOfStock
                        ? "This product is currently out of stock"
                        : "This variant is currently out of stock"}
                    </p>
                  )}
                {noVariantAvailable && (
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    This product is temporarily unavailable
                  </p>
                )}
                {!isOutOfStock &&
                  allSelected &&
                  !selectedVariant &&
                  variantOptions.length > 0 && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      This option combination isn't available
                    </p>
                  )}
                {addToCartDisabledMessage && (
                  <p
                    id="add-to-cart-disabled-message"
                    className="text-xs text-muted-foreground text-center mt-2"
                  >
                    {addToCartDisabledMessage}
                  </p>
                )}
              </div>
            </>
          )}
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

const METADATA_VISIBLE_COUNT = 8;

function ProductMetadataBadges({
  vendor,
  tags,
  productType,
  showVendor = true,
  showTags = true,
  conditionCriteria,
}: {
  vendor?: string | null;
  tags?: string[] | null;
  productType?: string | null;
  showVendor?: boolean;
  showTags?: boolean;
  conditionCriteria?: ConditionCriteria;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!conditionCriteria) return null;

  // Only pass vendor/productType/tags — collections and category are shown as breadcrumbs.
  // Catalog-level `hideVendorsFromCreators` / `hideTagsFromCreators` suppress the
  // corresponding fields before badge construction (EPD-8604).
  const badges = buildConditionBadges(
    {
      vendor: showVendor ? vendor : undefined,
      productType,
      tags: showTags ? tags : undefined,
    },
    conditionCriteria,
  );

  if (badges.length === 0) return null;

  const sorted = [...badges].sort(
    (a, b) => (b.matches ? 1 : 0) - (a.matches ? 1 : 0),
  );
  const visible = expanded ? sorted : sorted.slice(0, METADATA_VISIBLE_COUNT);
  const hiddenCount = sorted.length - METADATA_VISIBLE_COUNT;

  return (
    <div className="flex flex-wrap gap-1.5 mb-6">
      {visible.map((badge, i) => (
        <span
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`${badge.prefix ?? "Tag"}-${badge.label}-${i}`}
          className={
            badge.matches
              ? "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-teal-100 text-teal-800 border border-teal-200"
              : "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground"
          }
        >
          {badge.prefix ?? "Tag"}: {badge.label}
        </span>
      ))}
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer bg-transparent border-0 p-0"
          onClick={() => setExpanded(true)}
        >
          +{hiddenCount} more
        </button>
      )}
      {expanded && sorted.length > METADATA_VISIBLE_COUNT && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer bg-transparent border-0 p-0"
          onClick={() => setExpanded(false)}
        >
          show less
        </button>
      )}
    </div>
  );
}
